import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { backendBaseUrl } from '../config/backend';

function ParticipantPanel({ participantId, color }) {
  const [roomId, setRoomId] = useState('debug-room-123');
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [errors, setErrors] = useState([]);
  const socketRef = useRef(null);

  // Extrai a fase atual do último STATE_CHANGE nas mensagens
  const currentPhase = messages
    .filter(msg => msg?.type === 'STATE_CHANGE')
    .slice(-1)[0]?.content?.phase || null;

  useEffect(() => {
    // Criar conexão
    const socket = io(backendBaseUrl, {
      transports: ['websocket']
    });

    socketRef.current = socket;

    // Listeners
    socket.on('connect', () => {
      console.log(`✅ [${participantId}] Conectado ao WebSocket`);
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log(`❌ [${participantId}] Desconectado do WebSocket`);
      setIsConnected(false);
    });

    socket.on('room-joined', ({ room, context, currentState }) => {
      console.log(`🎉 [${participantId}] ROOM_JOINED`, { room, currentState, context });
      if (context && Array.isArray(context)) {
        setMessages([...context]);
      }
    });

    socket.on('message', (msg) => {
      console.log(`📨 [${participantId}] MESSAGE recebida:`, msg);

      if (msg?.type === 'STATE_CHANGE') {
        console.log(`🔄 [${participantId}] STATE_CHANGE detectado:`, msg.content);
      }

      setMessages(prev => [...prev, msg]);
    });

    socket.on('reply', (data) => {
      console.log(`💬 [${participantId}] REPLY recebida:`, data);
      if (data?.context && Array.isArray(data.context)) {
        setMessages([...data.context]);
      }
    });

    socket.on('error', (err) => {
      console.error(`❌ [${participantId}] ERROR:`, err);
      setErrors(prev => [...prev, err]);
    });

    // Cleanup
    return () => {
      socket.disconnect();
    };
  }, [participantId]);

  const handleJoinRoom = () => {
    if (socketRef.current && isConnected) {
      console.log(`🚪 [${participantId}] Entrando na sala:`, roomId);
      socketRef.current.emit('join-room', roomId);
    }
  };

  const handleSendMessage = (messageContent) => {
    if (socketRef.current && isConnected) {
      console.log(`📤 [${participantId}] Enviando mensagem:`, messageContent);
      socketRef.current.emit('message', messageContent);
    }
  };

  const handleSendAction = (action, spokenContinuation = '') => {
    if (socketRef.current && isConnected) {
      const payload = {
        type: 'TURN_ACTION',
        action,
        spokenContinuation
      };
      console.log(`🎮 [${participantId}] Enviando ação:`, payload);
      socketRef.current.emit('message', payload);
    }
  };

  return (
    <div style={{
      flex: 1,
      padding: '15px',
      border: `3px solid ${color}`,
      borderRadius: '8px',
      backgroundColor: '#f9f9f9'
    }}>
      <h2 style={{ margin: '0 0 15px 0', color }}>
        👤 {participantId}
      </h2>

      {/* Status da Conexão */}
      <div style={{
        padding: '8px',
        marginBottom: '15px',
        backgroundColor: isConnected ? '#d4edda' : '#f8d7da',
        border: `1px solid ${isConnected ? '#c3e6cb' : '#f5c6cb'}`,
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        <strong>Status:</strong> {isConnected ? '🟢 Conectado' : '🔴 Desconectado'}
      </div>

      {/* Fase Atual */}
      {currentPhase && (
        <div style={{
          padding: '8px',
          marginBottom: '15px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          🎯 Fase Atual: <span style={{ color: '#856404' }}>{currentPhase}</span>
        </div>
      )}

      {/* Join Room */}
      <div style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            placeholder="Room ID"
            style={{
              flex: 1,
              padding: '6px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          />
          <button
            type="button"
            onClick={handleJoinRoom}
            disabled={!isConnected}
            style={{
              padding: '6px 12px',
              backgroundColor: isConnected ? color : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            Join
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ marginBottom: '15px' }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
          📨 Messages ({messages.length})
        </div>
        <div style={{
          maxHeight: '200px',
          overflow: 'auto',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '4px',
          border: '1px solid #ddd'
        }}>
          {messages.length === 0 ? (
            <div style={{ color: '#6c757d', textAlign: 'center', fontSize: '11px' }}>
              Nenhuma mensagem...
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: '8px',
                  padding: '6px',
                  backgroundColor: msg?.type === 'STATE_CHANGE' ? '#fff3cd' : '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '3px'
                }}
              >
                <div style={{ fontSize: '9px', color: '#6c757d' }}>
                  #{idx + 1}
                </div>
                <pre style={{ fontSize: '10px', margin: '3px 0 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(msg, null, 2)}
                </pre>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{
          marginBottom: '15px',
          padding: '10px',
          border: '1px solid #dc3545',
          borderRadius: '4px',
          backgroundColor: '#f8d7da'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
            ❌ Errors ({errors.length})
          </div>
          <div style={{ maxHeight: '100px', overflow: 'auto', fontSize: '10px' }}>
            {errors.map((err, idx) => (
              <div key={idx} style={{ marginBottom: '3px' }}>
                {JSON.stringify(err)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
          ⚡ Turn Actions
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
          <button
            type="button"
            onClick={() => handleSendAction('A_COMPLETED', `test phrase from ${participantId}`)}
            disabled={!isConnected}
            style={{
              padding: '6px 8px',
              backgroundColor: isConnected ? '#28a745' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              fontSize: '10px',
              fontWeight: 'bold'
            }}
          >
            A Completed
          </button>
          <button
            type="button"
            onClick={() => handleSendAction('B_COMPLETED', `test phrase from ${participantId}`)}
            disabled={!isConnected}
            style={{
              padding: '6px 8px',
              backgroundColor: isConnected ? '#28a745' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              fontSize: '10px',
              fontWeight: 'bold'
            }}
          >
            B Completed
          </button>
          <button
            type="button"
            onClick={() => handleSendAction('A_ASKED', `question from ${participantId}`)}
            disabled={!isConnected}
            style={{
              padding: '6px 8px',
              backgroundColor: isConnected ? '#007bff' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              fontSize: '10px',
              fontWeight: 'bold'
            }}
          >
            A Asked
          </button>
          <button
            type="button"
            onClick={() => handleSendAction('B_ASKED', `question from ${participantId}`)}
            disabled={!isConnected}
            style={{
              padding: '6px 8px',
              backgroundColor: isConnected ? '#007bff' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              fontSize: '10px',
              fontWeight: 'bold'
            }}
          >
            B Asked
          </button>
          <button
            type="button"
            onClick={() => handleSendAction('AWKWARD_SILENCE', '')}
            disabled={!isConnected}
            style={{
              padding: '6px 8px',
              backgroundColor: isConnected ? '#ffc107' : '#ccc',
              color: '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              fontSize: '10px',
              fontWeight: 'bold',
              gridColumn: 'span 2'
            }}
          >
            Awkward Silence
          </button>
        </div>

        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
          🧪 Test Actions
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => handleSendMessage({ type: 'TEST', content: `Hello from ${participantId}` })}
            disabled={!isConnected}
            style={{
              padding: '6px 12px',
              backgroundColor: isConnected ? '#17a2b8' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected ? 'pointer' : 'not-allowed',
              fontSize: '10px'
            }}
          >
            Enviar Teste
          </button>
          <button
            type="button"
            onClick={() => setMessages([])}
            style={{
              padding: '6px 12px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '10px'
            }}
          >
            Limpar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SocketTest() {
  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h1 style={{ marginBottom: '20px' }}>🧪 Socket.IO Test - Multi Participant</h1>

      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <ParticipantPanel participantId="Participant A" color="#007bff" />
        <ParticipantPanel participantId="Participant B" color="#dc3545" />
      </div>

      {/* Console Info */}
      <div style={{
        padding: '10px',
        backgroundColor: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '4px',
        textAlign: 'center'
      }}>
        <strong>💡 Dica:</strong> Abra o DevTools Console para ver os logs detalhados de cada participante
      </div>
    </div>
  );
}
